/**
 * Zod schemas for the Jira integration (Phase 1).
 *
 * Issue/project key shapes live in `schemas/connection.ts` (they are needed by
 * the connection payload); this file covers the link + config API bodies.
 */
import { z } from 'zod';
import { jiraIssueKeySchema, jiraProjectKeySchema, JIRA_ISSUE_KEY_REGEX } from './connection.js';
// ── Link ──────────────────────────────────────────────────────────────────────
export const createJiraLinkSchema = z.object({
    jiraKey: jiraIssueKeySchema,
    setSlug: z.string().min(1).max(200),
    repo: z.string().min(1).max(200).optional(),
    branch: z.string().min(1).max(200).optional(),
});
// ── Config ────────────────────────────────────────────────────────────────────
export const jiraConfigSchema = z.object({
    projectKey: jiraProjectKeySchema.optional(),
    statusByStageId: z.record(z.string().min(1), z.string().min(1).max(100)).default({}),
    defaultStatus: z.string().min(1).max(100).optional(),
    comment: z.boolean().optional(),
    /** Phase 2 — the field holding the component template, by display name. */
    templateFieldName: z.string().min(1).max(200).optional(),
    /** Statuses Stride picks up as "ready to deploy" candidates (read-only intent). */
    pickupStatuses: z.array(z.string().min(1).max(100)).max(20).optional(),
});
// ── Create branch from story (Phase 2) ────────────────────────────────────────
export const createStoryBranchSchema = z.object({
    /** Pipeline whose SOURCE stage supplies the components + the base branch. */
    pipelineSlug: z.string().min(1).max(200),
    /** Stage id to build from. Defaults to the pipeline's first stage. */
    stageId: z.string().min(1).max(200).optional(),
    /** Override the derived `<KEY>-<summary-slug>` set name. */
    name: z.string().min(1).max(200).optional(),
    author: z.string().min(1).max(200).optional(),
});
/**
 * Reconcile a story's branch to its template (Phase 3).
 * `apply` defaults to FALSE — a reconcile can remove files, so the caller has to
 * ask for it explicitly rather than getting it from a bare POST.
 */
export const reconcileStorySchema = createStoryBranchSchema
    .omit({ name: true })
    .extend({
    apply: z.boolean().optional(),
    /** Reconcile even when the template hash is unchanged. */
    force: z.boolean().optional(),
});
// ── Key inference ─────────────────────────────────────────────────────────────
/**
 * Derive a Jira issue key from a set slug / branch name.
 *
 * Phase 2 creates branches as `feature/<KEY>-<slug>`, so once that lands the key
 * is recoverable from the branch alone. Phase 1 uses it opportunistically: a set
 * already named `KAN-12-widget` links without the operator retyping the key.
 * Returns null when no key is present — never guesses.
 */
export function inferJiraKeyFromSlug(slug) {
    const bare = slug.replace(/^feature\//, '');
    // Take the leading `<LETTERS>-<digits>` segment and test it in full, so
    // "KAN-12-widget" matches but "WIDGET-2FA-thing" does not silently become a key.
    const match = /^([A-Za-z][A-Za-z0-9]{1,9}-[0-9]{1,10})(?:-|$)/.exec(bare);
    if (!match?.[1])
        return null;
    const candidate = match[1].toUpperCase();
    return JIRA_ISSUE_KEY_REGEX.test(candidate) ? candidate : null;
}
// ── Scope editor (slice 2) ────────────────────────────────────────────────────
/** One declared row the operator is asking Stride to store. */
export const scopeRowSchema = z.object({
    lane: z.enum(['metadata', 'arm']),
    /** Metadata type, or ARM object. */
    type: z.string().min(1).max(120),
    /** Component fullName, or the ARM identifier (GlobalKey or a human key). */
    component: z.string().min(1).max(300),
});
/**
 * Replace a story's declared scope.
 *
 * `expectedHash` is optimistic concurrency: it must be the hash the editor loaded.
 * Omitting it FORCES the write, which is only right for a first write or a migration
 * — the route requires it whenever the story already has a scope.
 */
export const putScopeSchema = z.object({
    rows: z.array(scopeRowSchema).max(500),
    expectedHash: z.string().min(1).max(64).optional(),
});
// ── Releases (Phase 4) ────────────────────────────────────────────────────────
/** A release slug: lowercase, url/branch-safe. */
export const releaseSlugSchema = z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9][a-z0-9-]*$/, {
    message: 'Release name must be lowercase alphanumerics and dashes, e.g. "2026-08-sprint-3"',
});
export const composeReleaseSchema = z.object({
    /** Display name; the slug is derived from it when `slug` is absent. */
    name: z.string().min(1).max(120),
    slug: releaseSlugSchema.optional(),
    /** Member story keys. An empty list is refused — a release of nothing is a mistake. */
    members: z.array(jiraIssueKeySchema).min(1),
    /** Pipeline whose source stage supplies the base branch. */
    pipelineSlug: z.string().min(1).max(200),
    stageId: z.string().min(1).max(200).optional(),
    author: z.string().min(1).max(200).optional(),
});
/**
 * Change a release's membership and recompose.
 *
 * Back-out is a REBUILD: the branch is recreated from the members that remain,
 * rather than reverting a merge. Reverting keeps history but degrades once later
 * merges touch the same components, and a release branch is disposable by design.
 */
export const updateReleaseMembersSchema = z.object({
    members: z.array(jiraIssueKeySchema).min(1),
    pipelineSlug: z.string().min(1).max(200),
    stageId: z.string().min(1).max(200).optional(),
    author: z.string().min(1).max(200).optional(),
});
//# sourceMappingURL=jira.js.map